import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import trim from 'lodash/trim';
import unionBy from 'lodash/unionBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { LabelValueObject } from 'src/components/common/Types/CommonTypes';
import { DateFormats } from './Constants';

function StateMerger<T>(oldState: T, newState: Partial<T>): T {
  const mergedObject = {
    ...oldState,
    ...newState,
  };
  return mergedObject;
}

function stops(e) {
  e.preventDefault();
  e.stopPropagation();
}


function isValidArray(inputArray) {
  if (!inputArray) return false;
  if (Array.isArray(inputArray) && inputArray.length) return true;
  return false;
}

function isValidArrayIndex(idx) {
  return !isNaN(idx) && idx > -1;
}

function isValidString(inputString) {
  if (!inputString) return false;
  if (typeof inputString === 'string') return true;
  return false;
}

function isValidNumber(inputNumber) {
  if (!inputNumber && inputNumber !== 0) return false;
  if (!isNaN(inputNumber)) return true;
  return false;
}

function isValidBoolean(inputBoolean) {
  return typeof inputBoolean === 'boolean';
}

function getFormattedDateTime(formatString: string, epcoh: moment.MomentInput = moment()) {
  return moment(epcoh).format(formatString);
}

function getFromNow(epcoh: moment.MomentInput): string {
  return moment(epcoh).fromNow();
}

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

function getUniqFromArray<T>(inputArray: T[], key: string) {
  return uniqBy(inputArray, key);
}

function validatePositiveInt(rule, value, cb) {
  if (value < 0) {
    return cb('Enter a positive number');
  }
  cb();
}

const formatPriceAfterMillion = (amount) => {
  if (amount >= 1.0e9) {
    return `${Number(amount / 1.0e9).toFixed(2)}B`;
  }
  if (amount >= 1.0e6) {
    return `${Number((amount / 1.0e6).toFixed(2))}M`;
  }
  return amount;
};

function equal<T>(first: T, second: T): boolean {
  return isEqual(first, second);
}

function clone<T>(element: T): T {
  return cloneDeep(element);
}

function trimString(str: string): string {
  return trim(str);
}

function uniqArray<T>(array: T[]): T[] {
  return uniq(array);
}

function differenceArray<T>(first: T[], second: T[]): T[] {
  return difference(first, second);
}

function union<T>(first: T[], second: T[], key: string): T[] {
  return unionBy(first, second, key);
}

const convertToLabelValueArray = (array) => {
  const checkboxArray: LabelValueObject[] = [];
  array?.forEach((item) => {
    checkboxArray.push({
      value: item.key,
      label: item.label,
    });
  });
  return checkboxArray;
};

function getDateValue(date) {
  return date ? moment(date) : undefined;
}

const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const getCargoAndProduct = (masterCargoList, val) => {
  const cargoProductMaster = masterCargoList.find((item) => {
    return item.key?.cargo === val?.cargo &&
      item.key?.product === val?.product;
  });
  return cargoProductMaster;
}

const getContainer = (masterContainerList, val) => {
  const containerMaster = masterContainerList.find((item) => {
    return item.key?.type === val?.type &&
      item.key?.size === val?.size;
  });
  return containerMaster;
}

const sortByDate = (sortByOrder, list, key) => {
  list.sort((a, b) => {
    if (a[key] === b[key]) {
      return 0;
    }
    else if (a[key] === null) {
        return 1;
    }
    else if (b[key] === null) {
        return -1;
    }
    else if (sortByOrder === 'ASC') {
        return moment(a[key]).diff(b[key]);
    }
    else {
      return moment(b[key]).diff(a[key]);
    }
  });
};

const highlightDiv = (doc, color, timeout) => {
  var docBackground = doc.style.backgroundColor;
  doc.style.backgroundColor = color;
  setTimeout(() => {
    doc.style.backgroundColor = docBackground;
  }, timeout);
}

const checkForEquality = (newVal, oldVal) => {
  if (newVal instanceof Array) {
    return JSON.stringify(newVal) === JSON.stringify(oldVal);
  }
  return newVal === oldVal;
};

const walkObj = (obj: any, keyPath: string[], cb: (obj: any, keyPath: string[]) => void) => {
  cb(obj, keyPath);
  if (!obj) return;
  if (isValidArray(obj)) { // array
    return obj.map((element, idx) => {
      walkObj(element, [...keyPath, idx.toString()], cb);
    });
  } else if (typeof obj === 'object') { // object
    return Object.keys(obj).map(key => {
      walkObj(obj[key], [...keyPath, key], cb);
    });
  } else { // neither array, nor object
    //
  }
};

const setNestedKey = (obj: any, keyPath: string[], value: any) => {
  getNestedKey(obj, keyPath, true);
  return keyPath.reduce((acc, key, idx) => {
    if (idx === keyPath.length - 1) {
      acc[key] = value;
    }
    return acc[key];
  }, obj);
};

const getNestedKey = (obj: any, keyPath: string[], createIfEmpty = false) => {
  return keyPath.reduce((acc, key, idx) => {
    if (createIfEmpty && !acc[key]) {
      acc[key] = idx === keyPath.length - 1 ? null : {};
    }
    return acc && acc[key];
  }, obj);
};

const getObjDiff = <N, O>(newObj: N, oldObj: O): Partial<N | O> => {
  const returnObject = {};
  walkObj(newObj, [], (ob, kp) => {
    if (ob instanceof Object && !(ob instanceof Array)) {
      if (!getNestedKey(oldObj, kp)) {
        setNestedKey(returnObject, kp, getNestedKey(newObj, kp))
      } else {
        //
      }
    } else {
      if (!checkForEquality(ob, getNestedKey(oldObj, kp))) {
        setNestedKey(returnObject, kp, getNestedKey(newObj, kp));
      }
    }
  });
  walkObj(oldObj, [], (ob, kp) => {
    if (ob && !getNestedKey(newObj, kp)) {
      setNestedKey(returnObject, kp, null)
    }
  });
  return returnObject;
};

const getPropertyValue = (data, keyToRetrive) => {
  return keyToRetrive.split('.').reduce((current, key) => {
    return current && current[key];
  }, data);
};

const validatePassword = (pass: string) => {
  const regex = /^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{8,}$/;
  return regex.test(pass);
};

export const formRules = {
  required: {
    required: true,
    message: 'Required',
  },
  pattern: {
    pattern: /^[^\s]+$/,
    message: 'No whitespace allowed',
  },
  max: {
    max: 50,
    message: 'Max length allowed is 50',
  },
};

const getLabelFromKey = (key, arrayList) => {
  return arrayList?.find(item => item.key === key)?.label;
}

const dateStringToMoment = (value) => {
  return moment(value, DateFormats, true);
};

const upperFirst = (str) => {
  return str.slice(0, 1).toUpperCase() + str.slice(1, str.length);
}

const snakeToPretty = (str) => {
  if (!str) {
    return str;
  }
  const pretty = str.replace(
  /([-_][a-z])/g,
  (group) => group.toUpperCase()
  .replace('-', ' ')
  .replace('_', ' '));
  return upperFirst(pretty);
}

/**
 * @summary this function merge containers with same type and size
 * with their counts being added 
 * 
 * @param containerList : array of containers
 */
const groupContainersByTypeAndSize = (
  containerList: {
    type?: string;
    size?: string;
    count?: number | string;
  }[],
) => {
  const groupedArray = groupBy(containerList, ({ type = '', size = '' }) => {
    return type + size;
  });

  return Object.keys(groupedArray)
    .map(key => {
      return groupedArray[key].reduce((acc, container) =>
      ({
        ...acc,
        count: Number(acc.count || 0) + Number(container.count || 0)
      })
      )
    }).filter(ele => ele);
}

const unionSet = (sets: Set<any>[]) : Set<any> => {
  const newSet = new Set();
  sets.forEach(set => set.forEach(elem => newSet.add(elem)))
  return newSet;
}

const isJSON = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (err) {
    return false;
  }
}

const removeUndefinedWithNull = (body: Record<string, any>) => {
  Object.keys(body).forEach((key) => {
    _.isUndefined(body[key]) ?  body[key] = null : '';
  });
  return body;
};

const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
};

export {
  clone, convertToLabelValueArray, dateStringToMoment, differenceArray, equal, formatPriceAfterMillion, getCargoAndProduct,
  getContainer, getDateValue, getFormattedDateTime, getFromNow, getLabelFromKey, getNestedKey, getObjDiff,
  getPropertyValue, getUniqFromArray, groupContainersByTypeAndSize, highlightDiv, isJSON, isValidArray,
  isValidArrayIndex, isValidBoolean, isValidNumber, isValidString, onlyUnique, removeUndefinedWithNull, setNestedKey, sleep, snakeToPretty, sortByDate, StateMerger,
  stops, toTitleCase, trimString, union, unionSet, uniqArray, validatePassword, validatePositiveInt, walkObj
};

